import { useSelector } from 'react-redux'

import { faPlus } from '@fortawesome/pro-solid-svg-icons'

import { selectTranslate } from '../../../../reducers/LocaleReducer/LocalReducer'
import IconButton from '../../../antd/Buttons/IconButton'
import { Institution } from '../../../../utils/entities/institution'
import InstitutionsSelector from './InstitutionsSelector'
import InstitutionModal from '../../../../Providers/Forms/InstitutionModal/InstitutionModal'
import { ModalTypes } from '../../../../Providers/Forms/IndividualInternshipRequest/IndividualInternshipRequestModal'
import { selectUser } from '../../../../reducers/UserReducer'
import { getAllInstitutions } from '../../../../utils/api/institution'
import { useEffect, useState } from 'react'
import { Alert } from 'antd'
import Loading from '../../Loading'
import { isArray } from 'lodash'
import { getIdOrNull, isUUID } from '../../../../utils'

const PAGE_SIZE = 25

/** `values` also carries the free text name of institutions that are not registered yet, which is no id to fetch. */
const isInstitutionId = value => Number.isFinite(value) || isUUID(value)

const ExtendedInstitutionsSelector = ({
  canCreateInstitution = true, className = '', disabled, dropdownDisabled, multiple = 'default', school = null, style = {}, values,
  onChange, onInstitutionSubmit, renderOption
}) => {
  const t = useSelector(selectTranslate)
  const user = useSelector(selectUser)

  const [institutionEntities, setInstitutionEntities] = useState([])
  const [moreInstitutionsCount, setMoreInstitutionsCount] = useState(0)
  const [searchValue, setSearchValue] = useState('')
  const [debounceSearch, setDebounceSearch] = useState('')
  const [loadingInstitutions, setLoadingInstitutions] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebounceSearch(searchValue)
    }, 200)

    return () => clearTimeout(timer)
  }, [searchValue])

  const schoolId = getIdOrNull(school)

  useEffect(() => {
    const loadingTimer = setTimeout(() => setLoadingInstitutions(true), 200)
    const additionalIds = (isArray(values) ? values : [values]).filter(isInstitutionId)
    /**
     * An empty array would still be serialized, as a dangling separator carrying no id. The school narrows what a
     * student is offered down to what that very school reaches.
     */
    const parameters = {
      page: 1,
      pageSize: PAGE_SIZE,
      search: debounceSearch,
      additionalIds: additionalIds.length > 0 ? additionalIds : undefined,
      school: schoolId ?? undefined
    }

    getAllInstitutions(user, parameters).then(({ data, meta }) => {
      /**
       * The selector falls back on the whole context list when it is handed anything but an array, which would
       * display every institution at once : the fetched page is the only list this selector ever proposes.
       */
      setInstitutionEntities(isArray(data) ? data : [])

      if (meta?.pages?.total) {
        const total = meta.pages.total
        setMoreInstitutionsCount(Math.max(total - PAGE_SIZE, 0))
      } else {
        setMoreInstitutionsCount(0)
      }
    }).catch(() => {
      setInstitutionEntities([])
      setMoreInstitutionsCount(0)
    }).finally(() => {
      clearTimeout(loadingTimer)
      setLoadingInstitutions(false)
    })
  }, [user, debounceSearch, schoolId, values])

  const renderDropdown = onClick => menu => (
    <div>
      {moreInstitutionsCount > 0 &&
        <Alert
          className='more-message'
          message={t('form.request.institutions_count_more', { count: moreInstitutionsCount })}
          type='info'
          showIcon
          banner
        />}
      {/** A student only reaches the institutions his schools opened to him, which can legitimately be none. */}
      {!loadingInstitutions && institutionEntities.length === 0 && debounceSearch === '' &&
        <Alert
          className='more-message'
          message={t('form.request.no_institution_available')}
          type='info'
          showIcon
          banner
        />}
      {loadingInstitutions ? (
        <div className='p-10'>
          <Loading loading={loadingInstitutions} style={{ height: '100px' }} />
        </div>
      ) : menu}
      {canCreateInstitution && (
        <div className='flex-col m-4' onMouseDown={e => e.preventDefault()}>
          <IconButton
            text={t('form.request.new_institution')}
            icon={faPlus}
            onClick={() => onClick(handler => ({ ...handler, data: new Institution(), callback: onInstitutionSubmit, type: ModalTypes.STUDENT }))}
          />
        </div>
      )}
    </div>
  )

  return (
    <InstitutionModal>
      <InstitutionsSelector
        className={className}
        disabled={disabled}
        multiple={multiple}
        institutions={institutionEntities}
        style={style}
        values={values}
        dropdownRender={!dropdownDisabled && renderDropdown}
        onChange={onChange}
        renderOption={renderOption}
        loading={loadingInstitutions}
        onSearch={value => setSearchValue(value)}
      />
    </InstitutionModal>
  )
}

export default ExtendedInstitutionsSelector
